import { MockRestCall } from "@/common/api/ApiResult.model";
import { RestCallsKey } from "@/mock/demo/rest.resolver";
import {
  PlaybookDataDescriptor,
  PlaybookDataExecutionStatus,
  PlaybookDataRuntimeScope,
  PlaybookDataScope,
  PlaybookDescriptor,
} from "./model";
import { UseSimpleCarousel } from "@/common/composition/use-carousel";

const status_carousel = UseSimpleCarousel<PlaybookDataExecutionStatus>([
  "Building",
  "Ready",
  "Finished",
  "Finishing",
  "Stopped",
  "New",
  "Terminated",
  "Running",
]);

// @ts-ignore
const calls: Record<RestCallsKey, MockRestCall> = {
  "cloud-playbooks.all": {
    delay: 1000,
    payload: async (): Promise<PlaybookDescriptor[]> => {
      return [
        {
          id: "mpip",
          posture: "data_protection",
          action:
            "Automate MPIP Sensitivity Labels on Microsoft 365 Information At Rest",
          cascading_threats: [],
          threats: ["Data Leaks", "Compliance Violation"],
          is_available: true,
          type: "cloud",
          reduce_risk: "high",
          group: "Data Protection",
          friction: "low",
          protection_tier: 1,
          description: `Run a cloud script to apply sensitivity labels using Cognni's information categories using <a class="playbook-desc-link" href='/microsoft-labeling' target='_blank'>Cognni Labeling Automation Rules</a>`,
          criteria: {
            findingTypes: ["sensitive_information"],
            labelIds: [],
            serviceId: "msft_365",
            applicationId: "msft_onedrive",
          },
          display: {
            template_title: `Apply MPIP Sensitivity labels on {~application} for {~label} information {~finding_type} detection`,
            long_desc: `This policy involves the automation of sensitivity labels,
             leveraging Cognni’s autonomous sensitive information recognition to automate MPIP labels to enforce data loss prevention (DLP) labeling rules. 
             The automation process is a key stride in embedding a DLP strategy within an organization’s data security framework.
             By categorizing sensitive information accurately, this policy enables the protection of critical data assets from potential
             threats such as data leaks and compliance violations, thus fortifying the organization's data security posture and compliance with security and regulatory standards.`,
            entity_type: "FILE",

            pseudo_script: `
# Pseudocode to Apply Sensitivity Labels using Cognni's Information Categories

# Function to invoke Cognni API and get the information categories and corresponding labels
Function Get-CognniInfoCategories {
  # API call to Cognni to get information categories
  # Assuming an API token is needed for authorization
  $apiToken = "<YOUR_API_TOKEN>"
  $headers = @{
      "Authorization" = "Bearer $apiToken"
      "Content-Type"  = "application/json"
  }

  $cognniCategories = Invoke-RestMethod -Uri "https://api.cognni.ai/information-categories" -Method Get -Headers $headers
  return $cognniCategories
}

# Function to apply labels using Microsoft's sensitivity labeling
Function Apply-MicrosoftSensitivityLabel {
  param (
      [string]$documentId,   # The ID of the document to label
      [string]$sensitivityLabel # The sensitivity label to apply
  )

  Set-SensitivityLabel -DocumentId $documentId -Label $sensitivityLabel
  Write-Host "Applied $sensitivityLabel label to document ID: $documentId"
}

# Main script execution
$cognniCategories = Get-CognniInfoCategories

foreach ($category in $cognniCategories) {
  $documents = Get-Documents -Category $category.Name # Placeholder cmdlet to get documents based on Cognni category
  foreach ($document in $documents) {
      Apply-MicrosoftSensitivityLabel -documentId $document.Id -sensitivityLabel $category.MicrosoftLabel
  }`,
          },
        },
      ];
    },
  },

  "cloud-playbooks.instances": {
    delay: 1000,
    payload: async (playbook_id: string): Promise<PlaybookDataDescriptor[]> => {
      const date = new Date().toISOString();
      return Array(10)
        .fill({})
        .map((_, index) => {
          status_carousel.next();
          return {
            createdAt: date,
            createdById: "id_" + index,
            createdByName: "user " + index,
            id: "id_" + index,
            playbookId: playbook_id,
            runtimeScope: {
              applicationId: "msft_onedrive",
              appId: "INTERNAL",
              serviceId: "motion_365",
              // running the script on the entities session
              sessionId: 11,
            },
            scope: {
              itemsCount: 0,
              // scan session
              sessionId: 10,
              findingTypes: ["sensitive_information"],
              labelIds: ["e2bd11ab-9869-5adc-f11c-7d05c7c6fc2b"],
              serviceId: "motion_365",
              applicationId: "msft_onedrive",
            },
            status: status_carousel.current_item.value,
            type: "Generated",
          };
        });
    },
  },
  "cloud-playbooks.instance-status": {
    delay: 200,
    payload: async (ids: string[]) => {
      return ids.map((id) => {
        return {
          id,
          status: status_carousel.next(),
        };
      });
    },
  },
  "cloud-playbooks.create-instance": {
    delay: 200,
    payload: async (p: {
      playbook_id: string;
      scope: PlaybookDataScope;
    }): Promise<PlaybookDataDescriptor> => {
      console.log("PAYLOAD::", p);
      return {
        createdAt: new Date().toISOString(),
        createdById: "id_user" + Math.random(),
        createdByName: "me",
        id: "id_" + Math.random(),
        playbookId: p.playbook_id,
        runtimeScope: {
          applicationId: p.scope.applicationId,
          serviceId: p.scope.serviceId,
          // running the script on the entities session
          sessionId: 11,
          appId: "INTERNAL",
        },
        scope: {
          // scan session
          itemsCount: 0,
          sessionId: 10,
          findingTypes: p.scope.findingTypes,
          labelIds: p.scope.labelIds,
          serviceId: p.scope.serviceId,
          applicationId: p.scope.applicationId,
        },
        status: "New",
        type: "Generated",
      };
    },
  },
  "cloud-playbooks.get-data-url": {
    delay: 100,
    payload: async () => {
      return "http://localhost:3001/cognni/download";
    },
  },
  "cloud-playbooks.cancel-instance-run": {
    delay: 500,
    payload: async (id: string) => {
      return true;
    },
  },
  "cloud-playbooks.run-instance": {
    delay: 1000,
    payload: async (id: string): Promise<PlaybookDataRuntimeScope> => {
      return {
        applicationId: "dsdsds",
        serviceId: "dsds",
        sessionId: 12,
        appId: "INTERNAL",
      };
    },
  },
  "cloud-playbooks.specific-instances": {
    delay: 1000,
    payload: async (p: {
      playbook_id: string;
      instances: PlaybookDataDescriptor[];
    }): Promise<PlaybookDataDescriptor[]> => {
      return p.instances.map((ins) => {
        return {
          ...ins,
          scope: {
            ...ins.scope,
            itemsCount: 100,
          },
        };
      });
    },
  },
};

// @ts-ignore
window.inject_mock_api(calls);
